import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ArrowUpTrayIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import CustomDialog from "../../../components/modals/CustomDialog/CustomDialog";
import { Button } from "../../../components/forms/Buttons/Button";
import { isBlankHtml, RichText } from "../../../components/QuestionFolders/ImportPreviewQuestion";
import { getApiErrorMessage } from "../../../utils/apiError";
import TestHeadingField, { headingErrorText } from "./TestHeadingField";
import { focusRing, quietButtonClass, Spinner } from "./ui";

const fieldClass =
  "block w-full rounded-lg border-slate-300 px-3 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15";
const quillClass =
  "rounded-lg bg-white shadow-sm [&_.ql-container]:!border-slate-300 [&_.ql-container]:rounded-b-lg [&_.ql-editor]:min-h-[8rem] [&_.ql-editor]:text-sm [&_.ql-toolbar]:!border-slate-300 [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:bg-slate-50";
const footerClass = "-mx-6 -mb-6 mt-6 flex flex-col-reverse gap-2 rounded-b-2xl border-t border-slate-200 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end";
const smallButtonClass = `inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 text-xs font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 enabled:hover:bg-slate-50 disabled:opacity-50 ${focusRing}`;
// A footer's quiet button, as tall as the footer's others (the shared Button).
const stayButtonClass = `inline-flex h-11 items-center justify-center rounded-lg px-4 text-sm font-medium text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10 ${focusRing}`;
const formOf = (test) => ({
  name: test?.name || "",
  description: test?.description || "",
  heading: test?.heading || "",
  calculator_is_allowed: !!test?.calculator_is_allowed,
  periodic_table_is_allowed: !!test?.periodic_table_is_allowed,
  additional_information: test?.additional_information || "",
});
// Quill 1 has no tables: any edit of information with a docx table saves it flattened to plain lines.
const hasTables = (html) => /<table[\s>]/i.test(html || "");

// Inside a dialog's content: gives the focus to `target` (a ref) once the content is there. Headless UI's own first
// focus would land in the information's editor (it puts a contenteditable first); it keeps a focus already inside
// the dialog, and this effect's microtask, queued before its own (a child's effect runs first), gets there first.
function InitialFocus({ target }) {
  useEffect(() => {
    queueMicrotask(() => target.current?.focus());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Name, description, page heading, calculator, periodic table and additional information of the test (a .docx
// replaces the latter). `focusField`: "heading" opens it on the page heading (the exam's Word asks for it), else it
// starts on the name.
export function EditTestDialog({ isOpen, test, focusField = null, onClose, onSave, onUploadFile }) {
  const [t] = useTranslation();
  const [form, setForm] = useState(() => formOf(test));
  const [initial, setInitial] = useState(() => formOf(test));
  // A server from before the Word export sends no heading and would drop it: the field is left out then.
  const knowsHeading = !!test && "heading" in test;
  // Quill rewrites HTML it did not produce (e.g. flattens docx tables) and reports that as an "api"
  // change: the information only counts as changed, and is sent, once the user has edited it.
  const [infoEdited, setInfoEdited] = useState(false);
  // Information with tables stays out of Quill until the user agrees to lose them.
  const [tablesUnlocked, setTablesUnlocked] = useState(false);
  const [errors, setErrors] = useState({});
  // After a refused save the first refused field takes the focus.
  const [errorFocus, setErrorFocus] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const fieldsRef = useRef(null);
  const nameRef = useRef(null);
  const headingRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const next = formOf(test);
    setForm(next);
    setInitial(next);
    setInfoEdited(false);
    setTablesUnlocked(false);
    setErrors({});
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (errorFocus) fieldsRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [errorFocus]);

  // Memoized: the stored HTML can be large (inline docx images), and the dialog re-renders on every keystroke.
  const storedHasTables = useMemo(() => hasTables(initial.additional_information), [initial.additional_information]);
  const infoLocked = storedHasTables && !tablesUnlocked;
  const unlockTables = () => {
    if (window.confirm(t("test_bank.editor.tables_edit_confirm"))) setTablesUnlocked(true);
  };

  // What the test will show: the stored information until the user edits it in Quill.
  const info = infoEdited ? form.additional_information : initial.additional_information;
  const dirty =
    infoEdited ||
    form.name !== initial.name ||
    form.description !== initial.description ||
    form.heading !== initial.heading ||
    form.calculator_is_allowed !== initial.calculator_is_allowed ||
    form.periodic_table_is_allowed !== initial.periodic_table_is_allowed;
  const close = () => {
    if (saving || uploading) return;
    if (dirty && !window.confirm(t("test_bank.builder.discard_confirm"))) return;
    onClose();
  };
  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      setErrors({ name: t("test_bank.form.name_required") });
      setErrorFocus((n) => n + 1);
      return;
    }
    setSaving(true);
    try {
      const { additional_information, heading, ...fields } = form;
      await onSave({
        ...fields,
        name: form.name.trim(),
        description: form.description.trim(),
        // "" is a heading too: the Word goes back to the subject's title.
        ...(knowsHeading ? { heading: heading.trim() } : {}),
        ...(infoEdited ? { additional_information } : {}),
      });
      toast.success(t("test_bank.builder.test_saved"));
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      if (data && typeof data === "object" && !data.code && (data.name || data.description || data.heading)) {
        setErrors({
          name: [].concat(data.name || []).join(" "),
          description: [].concat(data.description || []).join(" "),
          heading: headingErrorText(t, data.heading),
        });
        setErrorFocus((n) => n + 1);
      } else {
        toast.error(getApiErrorMessage(t, err, "test_bank.editor.update_error"));
      }
    } finally {
      setSaving(false);
    }
  };
  // Enter in a one-line field saves, as in the create forms (Quill, the textarea and the buttons keep their own).
  const submit = (e) => {
    e.preventDefault();
    if (!saving && !uploading) save();
  };

  const chooseFile = () => {
    if (!isBlankHtml(info) && !window.confirm(t("test_bank.editor.confirm_replace_content"))) return;
    fileRef.current?.click();
  };
  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      toast.error(t("test_bank.form.only_docx"));
      return;
    }
    setUploading(true);
    try {
      const saved = await onUploadFile(file);
      const stored = saved?.additional_information || "";
      // The file is stored right away: it is no longer an unsaved change of this form.
      setForm((prev) => ({ ...prev, additional_information: stored }));
      setInitial((prev) => ({ ...prev, additional_information: stored }));
      setInfoEdited(false);
      setTablesUnlocked(false);
      toast.success(t("test_bank.editor.file_uploaded"));
    } catch (err) {
      toast.error(getApiErrorMessage(t, err, "test_bank.editor.upload_error"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <CustomDialog isOpen={isOpen} closeModal={close} headerText={t("test_bank.edit_modal.title")} maxWidth="max-w-3xl">
      <InitialFocus target={focusField === "heading" && knowsHeading ? headingRef : nameRef} />
      <form noValidate onSubmit={submit}>
        <div ref={fieldsRef} className="mt-4 space-y-5">
          <div>
            <label htmlFor="edit-test-name" className="mb-1.5 block text-sm font-medium text-slate-700">
              {t("test_bank.form.test_name")}
            </label>
            <input
              ref={nameRef}
              id="edit-test-name"
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder={t("test_bank.form.test_name_placeholder")}
              aria-invalid={!!errors.name}
              className={`${fieldClass} h-10 ${errors.name ? "border-red-300 focus:border-red-500 focus:ring-red-500/15" : ""}`}
            />
            {errors.name && (
              <p role="alert" className="mt-1.5 text-sm text-red-600">
                {errors.name}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="edit-test-description" className="mb-1.5 block text-sm font-medium text-slate-700">
              {t("test_bank.form.test_description")} <span className="font-normal text-slate-500">({t("test_bank.form.optional")})</span>
            </label>
            <textarea
              id="edit-test-description"
              rows={3}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder={t("test_bank.form.description_placeholder")}
              className={`${fieldClass} resize-none py-2`}
            />
            {errors.description && (
              <p role="alert" className="mt-1.5 text-sm text-red-600">
                {errors.description}
              </p>
            )}
          </div>
          {knowsHeading && (
            <TestHeadingField
              ref={headingRef}
              id="edit-test-heading"
              value={form.heading}
              onChange={(e) => update("heading", e.target.value)}
              error={errors.heading || ""}
            />
          )}
          <label className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300">
            <input
              type="checkbox"
              checked={form.calculator_is_allowed}
              onChange={(e) => update("calculator_is_allowed", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
            />
            <span className="text-sm font-medium text-slate-700">{t("test_bank.form.calculator_allowed")}</span>
          </label>
          <label className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300">
            <input
              type="checkbox"
              checked={form.periodic_table_is_allowed}
              onChange={(e) => update("periodic_table_is_allowed", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
            />
            <span className="text-sm font-medium text-slate-700">{t("test_bank.form.periodic_table_allowed")}</span>
          </label>
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-700">{t("test_bank.form.additional_information")}</span>
              {/* A secondary way to fill it in: quiet. */}
              <button type="button" onClick={chooseFile} disabled={uploading} className={`${quietButtonClass} -mr-3`}>
                {uploading ? <Spinner className="h-3.5 w-3.5" /> : <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />}
                {uploading ? t("test_bank.edit_modal.uploading") : t("test_bank.form.upload_docx")}
              </button>
              <input ref={fileRef} type="file" accept=".docx" onChange={uploadFile} className="hidden" />
            </div>
            {infoLocked ? (
              <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-900">{t("test_bank.editor.tables_locked")}</p>
                <button
                  type="button"
                  onClick={unlockTables}
                  disabled={uploading}
                  className={`${smallButtonClass} self-start sm:self-auto`}
                >
                  <PencilSquareIcon className="h-4 w-4 text-slate-500" aria-hidden="true" />
                  {t("test_bank.editor.tables_edit_anyway")}
                </button>
              </div>
            ) : (
              <>
                <ReactQuill
                  theme="snow"
                  value={form.additional_information}
                  onChange={(value, _delta, source) => {
                    // Quill's rewrite is stored too: a value that differs from Quill's own is set again on every render.
                    update("additional_information", value);
                    if (source === "user") setInfoEdited(true);
                  }}
                  readOnly={uploading}
                  className={quillClass}
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  <Trans i18nKey="test_bank.form.formulas_hint_code" components={{ code: <code /> }} />
                </p>
              </>
            )}
            {!isBlankHtml(info) && (
              <div className="mt-3">
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">{t("test_bank.form.preview")}</p>
                <div className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <RichText html={info} />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={footerClass}>
          <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={close} disabled={saving} />
          <Button type="submit" text={t("buttons.save")} color="primary" loading={saving} disabled={saving || uploading} />
        </div>
      </form>
    </CustomDialog>
  );
}

// Read-only additional information, with a shortcut to the edit dialog. Other read-only content (a
// passage) comes as `children` under its own `title`. `onClosed` once it has closed: a dialog opened from it waits
// for that, as this one's focus trap holds the focus while it fades out.
export function InfoDialog({ isOpen, title, html, children, onClose, onEdit, onClosed }) {
  const [t] = useTranslation();
  return (
    <CustomDialog isOpen={isOpen} closeModal={onClose} headerText={title || t("test_bank.form.additional_information")} maxWidth="max-w-3xl">
      <AfterClose open={isOpen} onClosed={onClosed} />
      <div className="mt-4 max-h-[60vh] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm leading-relaxed text-slate-800 [&_img]:max-h-80 [&_img]:w-auto">
        {children ?? <RichText html={html} />}
      </div>
      <div className={footerClass}>
        <Button type="button" text={t("buttons.close")} color="secondary-outline" onClick={onClose} />
        <Button type="button" text={t("buttons.edit")} Icon={PencilSquareIcon} color="primary" onClick={onEdit} />
      </div>
    </CustomDialog>
  );
}

// Calls `onClosed` when the dialog's content goes, that is once its closing transition is over. Not for the
// remount StrictMode makes in development: the dialog is still open then.
function AfterClose({ open, onClosed }) {
  const latest = useRef({ open, onClosed });
  latest.current = { open, onClosed };
  useEffect(() => {
    const box = latest;
    return () => {
      if (!box.current.open) box.current.onClosed?.();
    };
  }, []);
  return null;
}

// Asks before removing rows whose questions could not be added back for now: not approved, and their exam lets
// only approved questions into tests. `rows`: those of them that would go; `passage`: they go with it.
// `onClosed` once it has closed (the removal waits for it, so the focus can be put back after the dialog's own).
// It can be asked from the preview sheet (z-40): it is drawn above it, and rendered inside it then (see TestBuilder).
export function BlockedRemoveDialog({ isOpen, rows, passage, onCancel, onConfirm, onClosed }) {
  const [t] = useTranslation();
  const question = rows[0]?.question || {};
  return (
    <CustomDialog
      isOpen={isOpen}
      closeModal={onCancel}
      headerText={passage ? t("test_bank.builder.blocked_remove.passage_title") : t("test_bank.builder.blocked_remove.title")}
      maxWidth="max-w-md"
      zIndex="z-50"
    >
      <AfterClose open={isOpen} onClosed={onClosed} />
      <p className="mt-2 text-sm text-slate-600">
        {passage
          ? t("test_bank.builder.blocked_remove.passage_text", { name: passage.name || "", count: rows.length })
          : t("test_bank.builder.blocked_remove.text", { code: question.code || "", folder: question.folder?.parent?.name || "" })}
      </p>
      <div className={footerClass}>
        <Button type="button" text={t("buttons.cancel")} color="secondary-outline" onClick={onCancel} />
        <Button type="button" text={t("test_bank.builder.remove")} color="error-solid" onClick={onConfirm} />
      </div>
    </CustomDialog>
  );
}

// Leaving while questions are selected but not added yet: one main button (add, then leave); staying is quiet.
export function LeaveDialog({ isOpen, count, adding, onStay, onLeave, onAddAndLeave }) {
  const [t] = useTranslation();
  return (
    <CustomDialog isOpen={isOpen} closeModal={adding ? () => {} : onStay} headerText={t("test_bank.builder.leave_title")} maxWidth="max-w-md">
      <p className="mt-2 text-sm text-slate-600">{t("test_bank.builder.leave_text", { count })}</p>
      <div className={footerClass}>
        <button type="button" onClick={onStay} disabled={adding} className={stayButtonClass}>
          {t("test_bank.builder.stay")}
        </button>
        <Button type="button" text={t("test_bank.builder.leave_without_adding")} color="secondary-outline" onClick={onLeave} disabled={adding} />
        <Button type="button" text={t("test_bank.builder.add_and_leave")} color="primary" onClick={onAddAndLeave} loading={adding} disabled={adding} />
      </div>
    </CustomDialog>
  );
}
